"use client";

import Link from "next/link";
import { useState, type ReactNode } from "react";
import clsx from "clsx";

export type CourseCardProps = {
  /** Makes the entire card a single link to this destination. Omit to keep the card as a
   * plain container and wire up individual click targets via `imageHref`/`titleHref`/`footer`. */
  href?: string;
  /** Wraps only the image/placeholder area in a link. Ignored when `href` is set. */
  imageHref?: string;
  /** Wraps only the title in a link. Ignored when `href` is set. */
  titleHref?: string;
  image?: string | null;
  imageAlt: string;
  placeholder: ReactNode;
  category: string;
  title: string;
  description?: string;
  footer: ReactNode;
  /** Render the title above the category instead of below it (matches some legacy layouts). */
  titleFirst?: boolean;
  /** Classes for the outer card (border, background, shadow, hover, rounded corners). */
  className?: string;
  /** Classes for the image/placeholder wrapper (aspect ratio or fixed height, background). */
  imageWrapperClassName?: string;
  /** Extra classes for the <img> element itself (e.g. hover scale transitions). */
  imageClassName?: string;
  /** Classes for the content section padding. */
  contentClassName?: string;
  categoryClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
};

/**
 * Shared course card used by every course grid in the app. Every instance renders as a
 * flex column that fills the height of its grid cell, with a fixed-size image/placeholder
 * area, a 2-line-clamped title with reserved space, and a footer pinned to the bottom via
 * margin-top: auto — so cards in the same row always end up the same height regardless of
 * whether they have a cover image or how long their title is.
 *
 * Click targets are opt-in and preserve each caller's original interaction: pass `href` for
 * a whole-card link, or `imageHref`/`titleHref` (plus a `footer` containing its own link) to
 * keep only specific regions clickable.
 */
export default function CourseCard({
  href,
  imageHref,
  titleHref,
  image,
  imageAlt,
  placeholder,
  category,
  title,
  description,
  footer,
  titleFirst = false,
  className,
  imageWrapperClassName = "",
  imageClassName,
  contentClassName = "p-4",
  categoryClassName,
  titleClassName,
  descriptionClassName,
}: CourseCardProps) {
  // Track the src that failed to load rather than a boolean, so a card that
  // later re-renders with a different (working) image still tries to show it.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = image && failedSrc !== image;

  const imageContent = showImage ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={image}
      alt={imageAlt}
      loading="lazy"
      className={clsx("h-full w-full object-cover", imageClassName)}
      // A broken/404 cover falls back to the same placeholder an image-less
      // card shows, instead of the browser's broken-image glyph.
      onError={() => setFailedSrc(image)}
    />
  ) : (
    <div className="flex h-full w-full items-center justify-center">
      {placeholder}
    </div>
  );

  const imageWrapper = (
    <div
      className={clsx(
        "relative w-full shrink-0 overflow-hidden",
        imageWrapperClassName,
      )}
    >
      {!href && imageHref ? (
        <Link
          href={imageHref}
          aria-label={imageAlt}
          className="block h-full w-full cursor-pointer"
        >
          {imageContent}
        </Link>
      ) : (
        imageContent
      )}
    </div>
  );

  const categoryEl = (
    <p className={clsx("truncate text-xs font-semibold", categoryClassName)}>
      {category}
    </p>
  );

  const titleContent = (
    <h3
      className={clsx("line-clamp-2 leading-snug", titleClassName)}
      style={{ minHeight: "2em" }}
    >
      {title}
    </h3>
  );
  const titleEl =
    !href && titleHref ? (
      <Link href={titleHref}>{titleContent}</Link>
    ) : (
      titleContent
    );

  const content = (
    <div className={clsx("flex flex-1 flex-col", contentClassName)}>
      {titleFirst ? (
        <>
          {titleEl}
          <div>{categoryEl}</div>
        </>
      ) : (
        <>
          {categoryEl}
          <div className="mt-2">{titleEl}</div>
        </>
      )}

      {description ? (
        <p className={clsx("mt-2 line-clamp-2", descriptionClassName)}>
          {description}
        </p>
      ) : null}

      <div className="mt-auto">{footer}</div>
    </div>
  );

  const rootClassName = clsx("flex h-full w-full flex-col overflow-hidden", className);

  if (href) {
    return (
      <Link href={href} className={rootClassName}>
        {imageWrapper}
        {content}
      </Link>
    );
  }

  return (
    <div className={rootClassName}>
      {imageWrapper}
      {content}
    </div>
  );
}
